import { createFileRoute, Link } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import {
  HeartHandshake,
  MessagesSquare,
  Layers3,
  PenLine,
  ShieldCheck,
  Scale,
  ArrowRight,
  Users,
  Sparkles,
  Star,
  Quote,
  ChevronDown,
  Eye,
} from "lucide-react";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
  CarouselSwipeHint,
} from "@/components/ui/carousel";
import { LandingFooter } from "@/components/LandingFooter";
import { VERIFIED_TRUSTPILOT_REVIEWS } from "@/lib/trustpilot";
import { FAQ_ARTICLES } from "@/data/faq";
import { addRipple } from "@/lib/ripple";
import { track } from "@/lib/track";
import { startMembershipJourney, MEMBERSHIP_JOURNEY_PATH } from "@/lib/funnel";

const JOIN_OG =
  "https://storage.googleapis.com/gpt-engineer-file-uploads/attachments/og-images/bda540b3-55c7-4373-af6d-b5588ecb7c64";

export const Route = createFileRoute("/join-community")({
  head: () => ({
    meta: [
      { title: "Join the Community — Recovery Trauma™" },
      {
        name: "description",
        content:
          "Join the Recovery Trauma™ community — a survivor-led space to connect, learn and heal at your own pace. Read real member voices, get answers and start your recovery journey.",
      },
      { property: "og:title", content: "Join the Community — Recovery Trauma™" },
      {
        property: "og:description",
        content:
          "A survivor-led community for trauma recovery. Real voices, honest answers, and a calm place to begin.",
      },
      { property: "og:url", content: "https://recoverytrauma.com/join-community" },
      { property: "og:image", content: JOIN_OG },
      { name: "twitter:image", content: JOIN_OG },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: "https://recoverytrauma.com/join-community" }],
  }),
  component: JoinCommunityPage,
});

const COMMUNITY_POINTS = [
  {
    icon: MessagesSquare,
    title: "Trauma-recovery discussions",
    body: "Conversations about the things that come up in recovery, in a space made for them.",
  },
  {
    icon: Layers3,
    title: "Topic-based spaces",
    body: "Areas grouped by theme, so you can find what fits where you are today.",
  },
  {
    icon: PenLine,
    title: "Share when you're ready",
    body: "Share reflections and what has helped you — as much or as little as you want to.",
  },
  {
    icon: HeartHandshake,
    title: "Connection without pressure",
    body: "Read along quietly or take part; both are welcome.",
  },
  {
    icon: Scale,
    title: "Clear community boundaries",
    body: "Guidelines so the space stays supportive rather than overwhelming.",
  },
  {
    icon: ShieldCheck,
    title: "Moderation and safety",
    body: "Moderated by the Recovery Trauma™ team, with member privacy respected.",
  },
];

const FAQ_IDS = [
  "what-is-rt",
  "who-for",
  "account-vs-founding",
  "access-after-account",
  "how-know-active",
  "overwhelmed",
];

function StarRow({ rating }: { rating: number }) {
  return (
    <div className="flex items-center gap-0.5" aria-label={`${rating} out of 5 stars`}>
      {Array.from({ length: 5 }).map((_, i) => (
        <Star
          key={i}
          className={`h-4 w-4 ${
            i < rating ? "fill-[var(--gold-deep)] text-[var(--gold-deep)]" : "text-foreground/20"
          }`}
        />
      ))}
    </div>
  );
}

function TestimonialCard({ review }: { review: (typeof VERIFIED_TRUSTPILOT_REVIEWS)[number] }) {
  return (
    <div className="flex h-full flex-col rounded-[1.75rem] border-2 border-gold/30 bg-gradient-to-br from-card via-gold-light/20 to-card p-6 shadow-sm sm:p-7">
      <Quote className="h-7 w-7 text-[var(--gold-deep)]/70" aria-hidden />
      <StarRow rating={review.rating} />
      <p className="mt-3 flex-1 font-body text-base leading-relaxed text-foreground/90">
        “{review.body}”
      </p>
      <div className="mt-5 flex items-center gap-3">
        <div className="flex h-11 w-11 items-center justify-center rounded-full border-2 border-gold/40 bg-[var(--gradient-gold)] font-display text-sm text-foreground shadow-[var(--shadow-gold)]">
          {review.author.slice(0, 2).toUpperCase()}
        </div>
        <div className="min-w-0 flex-1">
          <div className="truncate font-display text-sm text-foreground">— {review.author}</div>
          <div className="truncate text-[11px] uppercase tracking-wider text-foreground/60">
            {review.location} · Verified on Trustpilot
          </div>
        </div>
      </div>
    </div>
  );
}

function FaqAccordion() {
  const [open, setOpen] = useState<Set<string>>(new Set());
  const faqs = useMemo(() => FAQ_ARTICLES.filter((a) => FAQ_IDS.includes(a.id)), []);

  const toggle = (id: string) => {
    setOpen((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  return (
    <div className="mx-auto max-w-3xl space-y-3">
      {faqs.map((faq) => {
        const isOpen = open.has(faq.id);
        return (
          <div
            key={faq.id}
            className="rounded-2xl border border-gold/20 bg-card/80 px-5 py-4 shadow-sm transition hover:border-gold/40"
          >
            <button
              type="button"
              onClick={() => toggle(faq.id)}
              aria-expanded={isOpen}
              className="flex w-full items-center justify-between gap-4 text-start"
            >
              <span className="font-display text-base text-foreground sm:text-lg">{faq.q}</span>
              <ChevronDown
                className={`h-5 w-5 shrink-0 text-[var(--gold-deep)] transition-transform ${
                  isOpen ? "rotate-180" : ""
                }`}
                aria-hidden
              />
            </button>
            {isOpen && (
              <p className="mt-3 text-sm leading-relaxed text-foreground/80 sm:text-base">
                {faq.a}
              </p>
            )}
          </div>
        );
      })}
    </div>
  );
}

function JoinCommunityPage() {
  return (
    <div className="relative">
      <div
        aria-hidden
        className="bg-blob-warm pointer-events-none absolute inset-x-0 top-0 -z-10 h-[700px] opacity-60"
      />

      <div className="mx-auto max-w-6xl px-4 py-10 sm:px-6 sm:py-16 lg:px-8">
        {/* Hero */}
        <header className="text-center">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-foreground px-3 py-1 text-[10px] font-semibold uppercase tracking-wider text-background">
            <Users className="h-3 w-3" /> Community
          </span>
          <h1 className="mt-4 font-display text-3xl leading-tight text-foreground sm:text-5xl lg:text-6xl">
            You're not alone on this path
          </h1>
          <p className="mx-auto mt-4 max-w-2xl text-base leading-relaxed text-foreground/75 sm:text-lg">
            Recovery Trauma™ is a survivor-led community where you can learn, reflect and connect
            at your own pace — without pressure, diagnosis or timelines.
          </p>
        </header>

        {/* Values carousel */}
        <section aria-labelledby="community-values-heading" className="mt-14 sm:mt-20">
          <div className="mb-6 text-center sm:mb-8">
            <h2
              id="community-values-heading"
              className="font-display text-xl leading-tight text-foreground sm:text-3xl"
            >
              A space made for recovery
            </h2>
            <p className="mt-2 text-sm text-foreground/70 sm:text-base">
              Connection, boundaries and safety — built in from the start.
            </p>
          </div>
          <Carousel
            opts={{ align: "start", loop: true }}
            className="w-full"
            aria-label="What the Recovery Trauma™ community offers"
          >
            <CarouselContent className="-ms-3 sm:-ms-4">
              {COMMUNITY_POINTS.map(({ icon: Icon, title, body }) => (
                <CarouselItem
                  key={title}
                  className="ps-3 basis-[85%] sm:basis-1/2 sm:ps-4 lg:basis-1/3"
                >
                  <article className="flex h-full flex-col gap-3 rounded-2xl border border-gold/20 bg-card/80 p-5 shadow-sm">
                    <span className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-gold/15 text-[var(--gold-deep)]">
                      <Icon className="h-5 w-5" strokeWidth={1.9} />
                    </span>
                    <h3 className="font-display text-base text-foreground">{title}</h3>
                    <p className="text-sm leading-relaxed text-foreground/70">{body}</p>
                  </article>
                </CarouselItem>
              ))}
            </CarouselContent>
            <CarouselSwipeHint className="mt-5" />
            <CarouselPrevious className="hidden sm:flex -start-2" />
            <CarouselNext className="hidden sm:flex -end-2" />
          </Carousel>
        </section>

        {/* Testimonials */}
        <section aria-labelledby="community-voices-heading" className="mt-16 sm:mt-24">
          <div className="mb-8 text-center">
            <span className="inline-flex items-center gap-1.5 rounded-full border border-gold/40 bg-card px-3 py-1 text-[10px] font-semibold uppercase tracking-wider text-foreground/80">
              <Sparkles className="h-3 w-3" /> Community voices
            </span>
            <h2
              id="community-voices-heading"
              className="mt-3 font-display text-2xl leading-tight text-foreground sm:text-4xl"
            >
              What our community is saying
            </h2>
            <p className="mx-auto mt-3 max-w-xl text-sm leading-relaxed text-foreground/70 sm:text-base">
              Real, verified voices from people who use Recovery Trauma™.
            </p>
          </div>

          <Carousel
            opts={{ align: "start", loop: VERIFIED_TRUSTPILOT_REVIEWS.length > 1 }}
            className="w-full"
            aria-label="Verified community testimonials"
          >
            <CarouselSwipeHint label="Swipe to read more" />
            <CarouselContent className="-ms-4">
              {VERIFIED_TRUSTPILOT_REVIEWS.map((review) => (
                <CarouselItem
                  key={review.author + review.date}
                  className="ps-4 basis-4/5 sm:basis-1/2 lg:basis-1/3"
                >
                  <TestimonialCard review={review} />
                </CarouselItem>
              ))}
            </CarouselContent>
            {VERIFIED_TRUSTPILOT_REVIEWS.length > 1 && (
              <>
                <CarouselPrevious className="hidden sm:flex -start-3" />
                <CarouselNext className="hidden sm:flex -end-3" />
              </>
            )}
          </Carousel>
        </section>

        {/* FAQs */}
        <section aria-labelledby="community-faq-heading" className="mt-16 sm:mt-24">
          <div className="mb-8 text-center">
            <span className="inline-flex items-center gap-1.5 rounded-full border border-gold/40 bg-card px-3 py-1 text-[10px] font-semibold uppercase tracking-wider text-foreground/80">
              <HeartHandshake className="h-3 w-3" /> Common questions
            </span>
            <h2
              id="community-faq-heading"
              className="mt-3 font-display text-2xl leading-tight text-foreground sm:text-4xl"
            >
              Before you join
            </h2>
            <p className="mx-auto mt-3 max-w-xl text-sm leading-relaxed text-foreground/70 sm:text-base">
              Honest answers about membership, access and what to expect.
            </p>
          </div>
          <FaqAccordion />
          <div className="mt-6 text-center">
            <Link
              to="/faq"
              className="inline-flex items-center gap-1.5 text-sm font-medium text-[var(--gold-deep)] underline-offset-4 hover:underline"
            >
              View all FAQs <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </section>

        {/* CTA */}
        <section className="mt-16 sm:mt-24">
          <div className="relative overflow-hidden rounded-[2rem] border border-gold/30 bg-gradient-to-br from-card via-gold-light/30 to-card px-6 py-10 text-center shadow-[var(--shadow-soft)] sm:px-10 sm:py-14">
            <div
              aria-hidden
              className="pointer-events-none absolute -end-10 -top-10 h-40 w-40 rounded-full bg-[radial-gradient(circle_at_center,var(--gold-deep),transparent_70%)] opacity-20 blur-2xl"
            />
            <h2 className="relative font-display text-2xl leading-tight text-foreground sm:text-4xl">
              Ready to join us?
            </h2>
            <p className="relative mx-auto mt-3 max-w-xl text-sm leading-relaxed text-foreground/75 sm:text-base">
              Take the next step at your own pace. Create your account, explore what members see,
              and decide if Recovery Trauma™ feels right for you.
            </p>
            <div className="relative mt-6 flex flex-col items-center gap-3 sm:flex-row sm:justify-center">
              <Link
                to={MEMBERSHIP_JOURNEY_PATH}
                onPointerDown={addRipple}
                onClick={() => {
                  track("cta_click", { cta: "join_community" }, { source: "join_community_page" });
                  startMembershipJourney("join_community", "join_community_page");
                }}
                className="group inline-flex min-h-[52px] w-full max-w-[320px] items-center justify-center gap-2 rounded-full bg-[var(--gold-deep)] px-6 text-sm font-bold uppercase tracking-wider text-white shadow-[var(--shadow-gold)] transition hover:-translate-y-0.5 hover:bg-[color:color-mix(in_oklab,var(--gold-deep)_90%,black)] active:scale-[0.98] sm:w-auto"
              >
                Join Recovery Trauma™
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
              </Link>
              <Link
                to="/explore"
                onClick={() =>
                  track("cta_click", { cta: "explore_as_guest" }, { source: "join_community_page" })
                }
                className="inline-flex min-h-[48px] w-full max-w-[320px] items-center justify-center gap-2 rounded-full border border-gold/50 bg-background px-6 text-sm font-semibold text-foreground transition hover:-translate-y-0.5 hover:bg-gold/10 active:scale-[0.98] sm:w-auto"
              >
                <Eye className="h-4 w-4" />
                Explore as Guest
              </Link>
            </div>
            <p data-commerce className="relative mt-4 text-xs text-foreground/60">
              No pressure. Cancel anytime. Secure payment by card or PayPal.
            </p>
          </div>
        </section>
      </div>

      <LandingFooter />
    </div>
  );
}
